import { CircleAlert, CircleCheck, X } from 'lucide-react';

import { cn } from '@/design-system/lib/cn';
import { type Toast, useStore } from '@/store/AppStore';

/** O que o "Desfazer" do aviso faz — ou `null`, sem o botão. */
function undoOf(toast: Toast, canUndo: boolean, undoLast: () => void): (() => void) | null {
  if (typeof toast.undo === 'function') return toast.undo;

  return toast.undo && canUndo ? undoLast : null;
}

/**
 * Aviso de rodapé da store, centralizado na parte de baixo da página. Toda alteração na
 * escala oferece "Desfazer" por 5s — e também as ações do membro em Minha agenda
 * (confirmar, "Não posso ir", datas em que não pode). No celular sobe acima da bottom nav.
 * O tom diz se deu certo (ícone verde) ou não (ícone vermelho, anunciado como alerta).
 */
export default function Toaster() {
  const { toast, canUndo, actions } = useStore();

  if (!toast) return null;

  const undo = undoOf(toast, canUndo, actions.undo);

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'fixed inset-x-0 bottom-22 z-[90] mx-auto flex w-fit max-w-[calc(100vw-40px)] items-center gap-3 rounded-block bg-ink py-2.5 pr-3 pl-4',
        'text-control text-surface-subtle shadow-toast md:bottom-5'
      )}
    >
      {toast.tone === 'success' ? (
        <CircleCheck aria-label="Concluído" className="size-4.5 shrink-0 stroke-2 text-status-complete-bar" />
      ) : null}
      {toast.tone === 'error' ? (
        <CircleAlert aria-label="Erro" className="size-4.5 shrink-0 stroke-2 text-status-conflict-bar" />
      ) : null}
      <span>{toast.message}</span>
      {undo ? (
        <button
          type="button"
          onClick={undo}
          className="h-7.5 rounded-control-sm bg-ink-strong px-3 text-small font-semibold text-white"
        >
          Desfazer
        </button>
      ) : null}
      <button
        type="button"
        onClick={actions.hideToast}
        aria-label="Fechar aviso"
        className="grid size-7 place-items-center text-ink-placeholder hover:text-white"
      >
        <X aria-hidden className="size-4 stroke-[1.8]" />
      </button>
    </div>
  );
}
